/**
 * PublicRecommendPage - Página pública para ex-empregadores recomendarem
 * 
 * Acesso via link: /recomendar/{invite_code}
 * Não requer login - qualquer pessoa pode acessar
 * 
 * Tripé HeveaTalent:
 * - Justiça: Amplia oportunidades de recomendação
 * - Segurança: Validação de conteúdo positivo
 * - Simplicidade: Processo sem cadastro
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, AlertCircle, Loader2, Send, User, Building2, Calendar, Star } from 'lucide-react';
import { useTranslation } from '../translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicRecommendPage = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  
  const [formData, setFormData] = useState({
    recommender_name: '',
    recommender_role: '',
    property_name: '',
    employment_period: '',
    message: ''
  });

  // Carregar dados do convite
  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/invitations/public/${inviteCode}`);
        const data = await response.json();
        
        if (response.ok) {
          setInviteData(data);
          // Pré-preencher nome se disponível
          if (data.employer_name) {
            setFormData(prev => ({ ...prev, recommender_name: data.employer_name }));
          }
        } else {
          setError(data.detail || t('pubRecErrInvalid'));
        }
      } catch (err) {
        setError(t('pubRecErrLoad'));
      } finally {
        setLoading(false);
      }
    };

    if (inviteCode) {
      fetchInvite();
    }
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recommender_name.trim()) {
      setError(t('pubRecErrName'));
      return;
    }

    if (!formData.recommender_role) {
      setError(t('pubRecErrRole'));
      return;
    }

    if (!formData.property_name.trim()) {
      setError(t('pubRecErrFarm'));
      return;
    }
    
    if (!formData.message.trim() || formData.message.length < 10) {
      setError(t('pubRecErrMsg'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations/public/${inviteCode}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || t('pubRecErrSend'));
      }
    } catch (err) {
      setError(t('pubRecErrConn'));
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="p-8 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('pubRecLoadingInvite')}</p>
        </Card>
      </div>
    );
  }

  // Error state (convite inválido)
  if (error && !inviteData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('pubRecInvalidTitle')}</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button variant="outline" onClick={() => navigate('/')}>
            {t('pubRecGoHome')}
          </Button>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('pubRecThanks')}</h1>
          <p className="text-gray-600 mb-2">
            {t('pubRecSuccessMsg')} <strong>{inviteData?.worker_name}</strong> {t('pubRecSuccessMsg2')}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t('pubRecSuccessVisible')}
          </p>
          <Button variant="outline" onClick={() => navigate('/')}>
            {t('pubRecKnow')}
          </Button>
        </Card>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🌿</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">HeveaTalent</h1>
          <p className="text-gray-500">{t('pubRecPlatform')}</p>
        </div>

        {/* Card principal */}
        <Card className="p-6">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              {inviteData?.worker_photo ? (
                <img 
                  src={inviteData.worker_photo} 
                  alt={inviteData.worker_name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User className="w-10 h-10 text-green-600" />
              )}
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              {inviteData?.worker_name}
            </h2>
            {inviteData?.worker_specialization && (
              <p className="text-gray-500 text-sm">{inviteData.worker_specialization}</p>
            )}
            <p className="text-green-600 mt-2">{t('pubRecRequestsRec')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1" />
                {t('pubRecYourName')} *
              </label>
              <input
                type="text"
                value={formData.recommender_name}
                onChange={(e) => setFormData({...formData, recommender_name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={t('pubRecNamePlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Star className="w-4 h-4 inline mr-1" />
                {t('pubRecYourRole')} *
              </label>
              <select
                value={formData.recommender_role}
                onChange={(e) => setFormData({...formData, recommender_role: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                required
                data-testid="recommender-role-select"
              >
                <option value="">{t('pubRecSelectRole')}</option>
                <option value="proprietario">{t('pubRecRoleOwner')}</option>
                <option value="tecnico_agricola">{t('pubRecRoleTech')}</option>
                <option value="gerente">{t('pubRecRoleManager')}</option>
                <option value="engenheiro_agronomo">{t('pubRecRoleEngineer')}</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">{t('pubRecRoleOnly')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Building2 className="w-4 h-4 inline mr-1" />
                {t('pubRecFarm')} *
              </label>
              <input
                type="text"
                value={formData.property_name}
                onChange={(e) => setFormData({...formData, property_name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={t('pubRecFarmPlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('pubRecPeriod')}
              </label>
              <input
                type="text"
                value={formData.employment_period}
                onChange={(e) => setFormData({...formData, employment_period: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={t('pubRecPeriodPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('pubRecYourRec')} *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                rows={4}
                placeholder={t('pubRecPlaceholder')}
                required
                minLength={10}
                maxLength={500}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {formData.message.length}/500 {t('pubRecChars')}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('pubRecSending')}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {t('pubRecSend')}
                </>
              )}
            </Button>

            <p className="text-xs text-gray-400 text-center">
              {t('pubRecInfoPrefix')} {inviteData?.worker_name}. {t('pubRecInfoSuffix')}
            </p>
          </form>
        </Card>

        <p className="text-center text-gray-400 text-xs mt-6">
          HeveaTalent © 2026 - {t('pubRecPlatform')}
        </p>
      </div>
    </div>
  );
};

export default PublicRecommendPage;
